/**
 * First-sign-in password change.
 *
 * Rendered *instead of* the application, not over it. The account's password is one an administrator
 * typed out and passed along, so until it is replaced the employee's actions cannot honestly be
 * attributed to them — and an audit trail that attributes an action to an account two people can
 * open is not an audit trail. A dialog would leave the screen behind it fetching and clickable.
 *
 * The rules shown are the server's: minimum length and nothing else. Composition rules
 * ("one capital, one symbol") shrink the search space more than they enlarge it and push people
 * towards `Password1!`, so the field asks for a long one and says so.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { PasswordInput } from "../ui/PasswordInput";
import { useSession } from "./SessionProvider";

/** Mirrors `MIN_PASSWORD_LENGTH` in `app/auth/service.py`. The server is the one that enforces it. */
export const MIN_PASSWORD_LENGTH = 12;

export interface ChangePasswordGateProps {
  title: string;
}

export function ChangePasswordGate({ title }: ChangePasswordGateProps): JSX.Element {
  const { t } = useTranslation("auth");
  const { changePassword, signOut, identity } = useSession();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Checked here as well as on the server so the employee is not told "wrong" after a round trip
  // for a mistake the form can see.
  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const submittable =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, next);
    } catch (caught) {
      setError(
        caught instanceof ApiError && typeof caught.detail === "string"
          ? caught.detail
          : t("changePassword.failed"),
      );
      setCurrent("");
      setNext("");
      setConfirm("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-6">
      <Card className="w-full max-w-form">
        <span className="text-label-sm uppercase tracking-wide text-textMuted">{title}</span>
        <h1 className="mt-1 text-heading-md text-textPrimary">
          {t("changePassword.heading")}
        </h1>
        <p className="mt-2 text-body-sm text-textMuted">
          {t("changePassword.firstSignIn")}
        </p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={submit} noValidate>
          <PasswordInput
            label={t("changePassword.current")}
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            autoFocus
            required
            dir="ltr"
            showLabel={t("password.show")}
            hideLabel={t("password.hide")}
          />
          <PasswordInput
            label={t("changePassword.new")}
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            required
            dir="ltr"
            hint={t("changePassword.rule", { count: MIN_PASSWORD_LENGTH })}
            {...(tooShort ? { error: t("changePassword.tooShort", { count: MIN_PASSWORD_LENGTH }) } : {})}
            showLabel={t("password.show")}
            hideLabel={t("password.hide")}
          />
          <PasswordInput
            label={t("changePassword.confirm")}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            dir="ltr"
            {...(mismatch ? { error: t("changePassword.mismatch") } : {})}
            showLabel={t("password.show")}
            hideLabel={t("password.hide")}
          />

          {error ? (
            <p role="alert" className="text-body-sm text-statusDanger">
              {error}
            </p>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            {/* An employee who cannot complete this needs a way out that is not closing the tab on
                a live session. */}
            <Button type="button" variant="ghost" onClick={() => void signOut()}>
              {t("session.signOut")}
            </Button>
            <Button type="submit" variant="primary" disabled={busy || !submittable}>
              {busy ? t("changePassword.saving") : t("changePassword.save")}
            </Button>
          </div>
        </form>

        {identity ? (
          <p className="mt-4 text-body-sm text-textMuted">
            {t("session.signedInAs")} <bdi>{identity.display_name}</bdi>
          </p>
        ) : null}
      </Card>
    </div>
  );
}
